import { createApi } from '@reduxjs/toolkit/query/react';
import {
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query';
import { setCredentials } from 'features/auth/authSlice';
import { UpdateAuthTokens } from 'features/auth/types';
import { Mutex } from 'async-mutex';
import { logoutThunk } from 'features/auth/thunks/logoutThunk';
import { getCurrentEnvironment } from 'utils/environmentUtils';
import { RootState } from './store';
import { API_PROD_URL, ENVIRONMENT_CONFIG } from './constants';
import { resolveLocalBackendRequest } from './backendRouting';
import { prepareApiHeaders } from '../utils/apiUtils';

interface Errors {
  access?: string[];
  eligibility?: string[];
  code?: string[];
}

export interface Error {
  data: { error: string; errors: Errors };
  status: number;
}

/**
 * Get the BaseUrl used for backend service calls.
 *
 * Since the member-web application can be used in multiple environments with different URLs
 * we use the host url itself to decide which environment we're operating in
 * - If the origin includes 'staging', it returns `API_STAGING_URL`.
 * - If the origin includes 'qa', it returns `API_QA_URL`.
 * - If the origin includes 'uat', it returns `API_UAT_URL`.
 * - If the origin includes 'demo', it returns `API_DEMO_URL`.
 * - If the origin includes 'training', it returns `API_TRAINING_URL`.
 * - If the origin includes 'auto', it returns `API_AUTO_URL`.
 * - If the origin includes 'dev', it returns `API_ORIGIN_URL`.
 * - If the origin includes 'devint', it returns `API_DEV_INT_URL`.
 * - Only environment without an environment-specific subdomain is production where it returns `API_PROD_URL`.
 *
 * @returns {string} The base URL for the API.
 */
export const getBaseUrl = (): string => {
  const env = getCurrentEnvironment();
  const matchedConfig = ENVIRONMENT_CONFIG.find((config) => config.identifier === env);

  // Return the matched URL or default to production URL if no match is found
  if (matchedConfig) {
    return matchedConfig.url;
  }

  // eslint-disable-next-line no-console
  console.warn(
    `No matching API URL found for environment "${env}". Falling back to production API URL.`,
  );
  return API_PROD_URL;
};

const isLocalOrigin = (): boolean => getCurrentEnvironment() === 'local';

export const getBaseUrlForRequest = (args: string | FetchArgs): string => {
  if (isLocalOrigin()) {
    const localBackendRequest = resolveLocalBackendRequest(args);

    if (localBackendRequest) {
      return localBackendRequest.baseUrl;
    }
  }

  return getBaseUrl();
};

export const getRequestArgsForRequest = (args: string | FetchArgs): string | FetchArgs => {
  if (!isLocalOrigin()) {
    return args;
  }

  return resolveLocalBackendRequest(args)?.args ?? args;
};

export const baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = (
  args,
  api,
  extraOptions,
) =>
  fetchBaseQuery({
    baseUrl: getBaseUrlForRequest(args),
    prepareHeaders: (headers, { getState }) => {
      const store = getState() as RootState;
      return prepareApiHeaders(headers, store);
    },
  })(getRequestArgsForRequest(args), api, extraOptions);

const tokenRefreshLock = new Mutex();
const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs, // Args
  unknown, // Result
  FetchBaseQueryError // Error
> = async (args, store, extraOptions) => {
  // Wait for any ongoing token refresh to complete
  await tokenRefreshLock.waitForUnlock();

  let result = await baseQuery(args, store, extraOptions);
  const authState = (store.getState() as RootState).auth;

  if (result.error && result.error.status === 401 && authState.refreshToken) {
    if (!tokenRefreshLock.isLocked()) {
      // Case where there is no ongoing token refresh
      const release = await tokenRefreshLock.acquire();
      try {
        // Refresh the token
        const refreshResult = await baseQuery(
          {
            url: '/keymaster/mobile/v1/token/refresh',
            method: 'POST',
            body: { refreshToken: authState.refreshToken },
          },
          store,
          extraOptions,
        );

        if (refreshResult.data) {
          // Token is valid -> store it and retry the original request
          store.dispatch(setCredentials(refreshResult.data as UpdateAuthTokens));
          result = await baseQuery(args, store, extraOptions);
        } else {
          // Token is invalid, log the user out
          store.dispatch(logoutThunk());
        }
      } finally {
        release();
      }
    } else {
      // Case where we've received multiple 401s and token refresh is ongoing
      await tokenRefreshLock.waitForUnlock();
      result = await baseQuery(args, store, extraOptions);
    }
  }
  return result;
};

// initialize an empty api service that we'll inject endpoints into later as needed
export const WellframeApi = createApi({
  baseQuery: baseQueryWithReauth as BaseQueryFn<string | FetchArgs, unknown, Error>,
  tagTypes: ['Message', 'MessageAttachment', 'Instruction', 'Task', 'ConsentModule', 'Stats'],
  endpoints: () => ({}),
});
